import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Mail, Phone, MapPin, Shield, Calendar, FileText, Activity, Loader, AlertCircle } from 'lucide-react';
import { RootState } from '../store/reducers';
import {
  fetchUserDetailRequest,
  fetchUserStatsRequest,
  fetchUserActiviteRequest,
  fetchUserDecisionsRequest,
  clearUserDetail,
} from '../store/users/actions';
import './UserDetail.css';

const roleColorMap: Record<string, { color: string; bg: string; label: string }> = {
  admin:       { color: '#7C3AED', bg: '#F5F3FF', label: 'Administrateur' },
  superadmin:  { color: '#E85D04', bg: '#FFF3E8', label: 'Super Admin' },
  agent:       { color: '#2563EB', bg: '#EFF6FF', label: 'Agent' },
  validateur:  { color: '#059669', bg: '#ECFDF5', label: 'Validateur' },
};

const statutLabels: Record<string, { label: string; color: string; bg: string }> = {
  brouillon:  { label: 'Brouillon',   color: '#6B7280', bg: '#F3F4F6' },
  a_verifier: { label: 'À vérifier',  color: '#D97706', bg: '#FFF7ED' },
  valide:     { label: 'Validé',       color: '#047857', bg: '#ECFDF5' },
  publie:     { label: 'Publié',       color: '#059669', bg: '#D1FAE5' },
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    userDetail, userDetailLoading, userDetailError,
    userStats, userStatsLoading,
    userActivite, userActiviteLoading,
    userDecisions, userDecisionsLoading,
  } = useSelector((state: RootState) => state.users);

  useEffect(() => {
    if (id) {
      dispatch(fetchUserDetailRequest(id));
      dispatch(fetchUserStatsRequest(id));
      dispatch(fetchUserActiviteRequest(id));
      dispatch(fetchUserDecisionsRequest(id));
    }
    return () => { dispatch(clearUserDetail()); };
  }, [dispatch, id]);

  if (userDetailLoading) {
    return (
      <div className="user-detail">
        <div className="ud-loading"><Loader size={32} className="ud-spinner" /> Chargement...</div>
      </div>
    );
  }

  if (userDetailError) {
    return (
      <div className="user-detail">
        <div className="ud-error">
          <AlertCircle size={32} />
          <span>Erreur lors du chargement de l'utilisateur.</span>
          <button className="ud-btn-back" onClick={() => navigate('/espace-admin/utilisateurs')}>Retour</button>
        </div>
      </div>
    );
  }

  if (!userDetail) {
    return (
      <div className="user-detail">
        <div className="ud-error">
          <span>Utilisateur non trouvé.</span>
          <button className="ud-btn-back" onClick={() => navigate('/espace-admin/utilisateurs')}>Retour</button>
        </div>
      </div>
    );
  }

  const u = userDetail;
  const displayName = u.first_name && u.last_name
    ? `${u.first_name} ${u.last_name}`
    : u.email;
  const initials = u.first_name && u.last_name
    ? `${u.first_name.charAt(0)}${u.last_name.charAt(0)}`.toUpperCase()
    : u.email.charAt(0).toUpperCase();
  const primaryRole = u.role?.[0]?.name || 'user';
  const rb = roleColorMap[primaryRole] || { color: '#6B7280', bg: '#F3F4F6', label: primaryRole };

  return (
    <div className="user-detail">
      {/* Header */}
      <div className="ud-header">
        <button className="ud-btn-back" onClick={() => navigate('/espace-admin/utilisateurs')}>
          <ArrowLeft size={18} />
        </button>
        <span className="ud-breadcrumb">
          <span className="ud-bread-link" onClick={() => navigate('/espace-admin/utilisateurs')}>Utilisateurs</span>
          <span className="ud-bread-sep">/</span>
          <span>{displayName}</span>
        </span>
      </div>

      {/* Profile card */}
      <div className="ud-profile-card">
        <div className="ud-avatar">{initials}</div>
        <div className="ud-profile-info">
          <h2 className="ud-name">{displayName}</h2>
          <div className="ud-meta-row">
            <span className="ud-role-badge" style={{ color: rb.color, background: rb.bg }}>
              <Shield size={12} /> {rb.label}
            </span>
            <span className={`ud-status ${u.is_active ? 'ud-active' : 'ud-inactive'}`}>
              {u.is_active ? 'Actif' : 'Inactif'}
            </span>
          </div>
          <div className="ud-contact-row">
            <span><Mail size={14} /> {u.email}</span>
            {u.phone && <span><Phone size={14} /> {u.phone}</span>}
            {u.address && <span><MapPin size={14} /> {u.address}</span>}
            {u.created_at && (
              <span><Calendar size={14} /> Membre depuis {new Date(u.created_at).toLocaleDateString('fr-FR')}</span>
            )}
          </div>
        </div>
      </div>

      <div className="ud-grid">
        {/* Stats */}
        <div className="ud-card">
          <h3 className="ud-card-title"><Activity size={16} /> Statistiques</h3>
          {userStatsLoading ? (
            <div className="ud-card-loading">Chargement...</div>
          ) : userStats ? (
            <div className="ud-stats-grid">
              <div className="ud-stat-item">
                <span className="ud-stat-value">{userStats.decisions_count}</span>
                <span className="ud-stat-label">Décisions assignées</span>
              </div>
              <div className="ud-stat-item">
                <span className="ud-stat-value">{userStats.charge}</span>
                <span className="ud-stat-label" style={{ color: '#D97706' }}>En charge</span>
              </div>
              <div className="ud-stat-item">
                <span className="ud-stat-value">{userStats.nombre_traitees}</span>
                <span className="ud-stat-label" style={{ color: '#059669' }}>Traitées</span>
              </div>
              <div className="ud-stat-item">
                <span className="ud-stat-value">{userStats.publiees}</span>
                <span className="ud-stat-label" style={{ color: '#047857' }}>Publiées</span>
              </div>
              <div className="ud-stat-item">
                <span className="ud-stat-value">{userStats.en_brouillon}</span>
                <span className="ud-stat-label" style={{ color: '#6B7280' }}>En brouillon</span>
              </div>
              <div className="ud-stat-item">
                <span className="ud-stat-value">{userStats.rejetees}</span>
                <span className="ud-stat-label" style={{ color: '#DC2626' }}>Rejetées</span>
              </div>
            </div>
          ) : (
            <p className="ud-empty">Aucune statistique disponible.</p>
          )}
        </div>

        {/* Activite */}
        <div className="ud-card">
          <h3 className="ud-card-title"><Calendar size={16} /> Activité récente</h3>
          {userActiviteLoading ? (
            <div className="ud-card-loading">Chargement...</div>
          ) : userActivite.length > 0 ? (
            <div className="ud-activite-list">
              {userActivite.slice(0, 10).map((a, i) => (
                <div key={i} className="ud-activite-item">
                  <div className="ud-activite-dot" />
                  <div className="ud-activite-content">
                    <span className="ud-activite-action">{a.action}</span>
                    {a.details && <span className="ud-activite-details">{a.details}</span>}
                    <span className="ud-activite-date">
                      {new Date(a.date).toLocaleDateString('fr-FR')} à {new Date(a.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="ud-empty">Aucune activité récente.</p>
          )}
        </div>
      </div>

      {/* User decisions */}
      <div className="ud-card ud-decisions-card">
        <h3 className="ud-card-title"><FileText size={16} /> Décisions de l'utilisateur</h3>
        {userDecisionsLoading ? (
          <div className="ud-card-loading">Chargement...</div>
        ) : userDecisions.length > 0 ? (
          <table className="ud-table">
            <thead>
              <tr>
                <th>N° Décision</th>
                <th>Juridiction</th>
                <th>Nature</th>
                <th>Date</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {userDecisions.slice(0, 20).map((d: any) => {
                const s = statutLabels[d.statut] || { label: d.statut, color: '#666', bg: '#f3f4f6' };
                return (
                  <tr key={d.id} onClick={() => navigate(`/espace-cndj/decisions/${d.id}`)} className="ud-row-clickable">
                    <td className="ud-td-numero">{d.numero || '—'}</td>
                    <td>{d.juridiction || '—'}</td>
                    <td>{d.nature_litige || d.matiere || '—'}</td>
                    <td>{d.date_decision ? new Date(d.date_decision).toLocaleDateString('fr-FR') : '—'}</td>
                    <td>
                      <span className="ud-badge" style={{ color: s.color, background: s.bg }}>{s.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="ud-empty">Aucune décision associée.</p>
        )}
      </div>
    </div>
  );
};

export default UserDetail;
